import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ImageOff, Loader2, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const srcsIn = (html) =>
  [...String(html || "").matchAll(/<img[^>]+src="([^"]+)"/gi)]
    .map((m) => m[1].replace(/&amp;/g, "&"))
    .filter((s) => s && !s.startsWith("data:"));

const LABELS = {
  missing: "the file has gone from our storage",
  dead: "the original has been taken down",
  blocked: "we can't reach that address",
  unknown: "our storage didn't answer",
  hotlinked: "still loading from another site",
};

/** Tells an editor, the moment they open an article, that a picture in it is
 *  dead — and offers to put it back rather than leaving them to hunt. */
export function KbBrokenImageWarning({ bodyHtml, onRewrite, onWalk }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  const check = useCallback(async (html) => {
    const srcs = srcsIn(html);
    if (!srcs.length) { setReport(null); return; }
    try {
      const { data } = await api.post("/kb/images/check", { srcs });
      setReport(data);
    } catch { setReport(null); }
  }, []);

  // Once, on open — re-checking on every keystroke would hammer the CDNs.
  useEffect(() => {
    if (bodyHtml === undefined || bodyHtml === null) return;
    check(bodyHtml);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyHtml === undefined]);

  const problems = report?.problems || [];
  if (hidden || !problems.length) return null;

  const fixable = problems.filter((p) => p.fixable).map((p) => p.src);
  const broken = problems.filter((p) => p.state !== "hotlinked");

  const fix = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/kb/images/fix", { srcs: fixable });
      const rewrites = data.rewrites || {};
      if (Object.keys(rewrites).length && onRewrite) onRewrite(rewrites);
      if (data.fixed) toast.success(`${data.fixed} picture${data.fixed === 1 ? "" : "s"} fixed`);
      (data.failed || []).forEach((f) => toast.warning(`Couldn't fix one: ${f.why}`));
      await check(bodyHtml);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't fix those");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="kb-broken-image-warning"
         className={`mb-3 rounded-lg border p-3 ${broken.length
           ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40"
           : "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40"}`}>
      <div className="flex items-start gap-2">
        {broken.length
          ? <ImageOff className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
          : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
            {broken.length
              ? `${broken.length} picture${broken.length === 1 ? " in this article isn't" : "s in this article aren't"} loading`
              : `${problems.length} picture${problems.length === 1 ? " is" : "s are"} still hosted somewhere else`}
          </div>
          <ul className="mt-1 space-y-0.5">
            {problems.slice(0, 6).map((p) => (
              <li key={p.src} className="truncate text-[11px] text-slate-600 dark:text-slate-300">
                <span className="font-medium">{p.name || p.src.slice(-42)}</span>
                {" — "}{p.why || LABELS[p.state] || p.state}
              </li>
            ))}
            {problems.length > 6 && (
              <li className="text-[11px] text-slate-500">…and {problems.length - 6} more</li>
            )}
          </ul>
          {!!fixable.length && (
            <Button size="sm" className="mt-2 h-7 text-xs" onClick={fix} disabled={busy}
                    data-testid="kb-broken-image-fix">
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Wand2 className="mr-1 h-3.5 w-3.5" />}
              Fix {fixable.length} of them
            </Button>
          )}
          {fixable.length < problems.length && onWalk && (
            <Button size="sm" variant="outline" className="ml-2 mt-2 h-7 text-xs"
              data-testid="kb-broken-image-walk"
              onClick={() => onWalk(problems.filter((p) => !p.fixable).map((p) => p.src))}>
              <ImageOff className="mr-1 h-3.5 w-3.5" />
              Replace {problems.length - fixable.length} by hand
            </Button>
          )}
          {fixable.length < problems.length && (
            <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              The rest need a new picture — “Replace by hand” takes you to each one in turn.
            </p>
          )}
        </div>
        <button type="button" onClick={() => setHidden(true)} aria-label="Dismiss"
                data-testid="kb-broken-image-dismiss"
                className="rounded p-1 text-slate-400 transition-colors hover:text-slate-700">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
